import React from 'react';
import { format, startOfWeek, addDays, startOfMonth, endOfMonth, startOfWeek as startOfWeekFn, endOfWeek, eachDayOfInterval, isSameDay, isToday, isSameMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../../utils/cn';


const UserCalendarGrid = ({ 
  currentDate, 
  viewMode, 
  events = [], 
  onRequestAppointment 
}) => {
  const timeSlots = Array.from({ length: 12 }, (_, i) => i + 8); // 8 AM to 7 PM

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events?.filter(event => 
      isSameDay(new Date(event?.start), date)
    );
  };

  // Get event style based on availability
  const getEventStyle = (event) => {
    if (event?.type === 'available') {
      return 'bg-success/20 border-success text-success-foreground hover:bg-success/30 cursor-pointer';
    } else {
      return 'bg-muted/50 border-muted-foreground/20 text-muted-foreground cursor-not-allowed';
    }
  };

  // Check if time slot is available for requests
  const isTimeSlotAvailable = (event) => {
    return event?.type === 'available' && event?.isPublic;
  };

  // Week View Component
  const WeekView = () => {
    const startWeek = startOfWeek(currentDate, { locale: ptBR });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(startWeek, i));

    return (
      <div className="calendar-week-view">
        {/* Header */}
        <div className="grid grid-cols-8 border-b border-border">
          <div className="p-4 border-r border-border text-sm font-medium text-muted-foreground">
            Horário
          </div>
          {weekDays?.map((day) => (
            <div 
              key={day?.toISOString()} 
              className={cn(
                "p-4 border-r border-border text-center",
                isToday(day) && "bg-primary/10"
              )}
            >
              <div className="text-sm font-medium text-foreground">
                {format(day, 'EEE', { locale: ptBR })}
              </div>
              <div className={cn(
                "text-2xl font-semibold mt-1",
                isToday(day) ? "text-primary" : "text-foreground"
              )}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>
        {/* Time Grid */}
        <div className="calendar-time-grid">
          {timeSlots?.map((hour) => (
            <div key={hour} className="grid grid-cols-8 border-b border-border min-h-[80px]">
              {/* Time Column */}
              <div className="p-4 border-r border-border text-sm text-muted-foreground">
                {format(new Date()?.setHours(hour, 0), 'HH:mm')}
              </div>

              {/* Day Columns */}
              {weekDays?.map((day) => {
                const dayEvents = getEventsForDate(day)?.filter(event => 
                  new Date(event?.start)?.getHours() === hour
                );

                const hasAvailableSlot = dayEvents?.some(event => isTimeSlotAvailable(event));

                return (
                  <div 
                    key={`${day}-${hour}`}
                    className={cn(
                      "event-slot border-r border-border p-2 relative transition-colors",
                      !hasAvailableSlot && "hover:bg-accent/10"
                    )}
                  >
                    {dayEvents?.length > 0 ? (
                      dayEvents?.map((event) => (
                        <div
                          key={event?.id}
                          className={cn(
                            "absolute inset-2 rounded-md border-2 p-2 transition-all flex flex-col justify-center",
                            getEventStyle(event)
                          )}
                          onClick={() => {
                            if (isTimeSlotAvailable(event)) {
                              onRequestAppointment({
                                date: day,
                                time: format(new Date(event?.start), 'HH:mm'),
                                duration: 60,
                                event
                              });
                            }
                          }}
                        >
                          <div className="text-xs font-medium text-center">
                            {event?.type === 'available' ? 'Disponível' : 'Ocupado'}
                          </div>
                          <div className="text-xs opacity-75 text-center">
                            {format(new Date(event?.start), 'HH:mm')} - {format(new Date(event?.end), 'HH:mm')}
                          </div>
                          {isTimeSlotAvailable(event) && (
                            <div className="text-xs text-center mt-1 opacity-75">
                              Clique para solicitar
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="absolute inset-2 flex items-center justify-center text-xs text-muted-foreground/50">
                        {/* Empty slot */}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Month View Component
  const MonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeekFn(monthStart, { locale: ptBR });
    const endDate = endOfWeek(monthEnd, { locale: ptBR });

    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const weeks = [];
    for (let i = 0; i < days?.length; i += 7) {
      weeks?.push(days?.slice(i, i + 7));
    }

    return (
      <div className="calendar-month-view">
        {/* Header */}
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays?.map((day) => (
            <div key={day} className="p-4 border-r border-border text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="calendar-days-grid">
          {weeks?.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 border-b border-border">
              {week?.map((day) => {
                const dayEvents = getEventsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const availableEvents = dayEvents?.filter(event => isTimeSlotAvailable(event));

                return (
                  <div 
                    key={day?.toISOString()}
                    className={cn(
                      "event-slot border-r border-border p-3 min-h-[120px] transition-colors",
                      !isCurrentMonth && "bg-muted/30",
                      isToday(day) && "bg-primary/10",
                      availableEvents?.length > 0 && "hover:bg-accent/10"
                    )}
                  >
                    <div className={cn(
                      "text-sm font-medium mb-2",
                      isToday(day) ? "text-primary" : isCurrentMonth ? "text-foreground" : "text-muted-foreground"
                    )}>
                      {format(day, 'd')}
                    </div>

                    <div className="space-y-1">
                      {dayEvents?.slice(0, 3)?.map((event) => (
                        <div
                          key={event?.id}
                          className={cn(
                            "text-xs p-1 rounded border transition-all truncate",
                            getEventStyle(event)
                          )}
                          onClick={() => {
                            if (isTimeSlotAvailable(event)) {
                              onRequestAppointment({
                                date: day,
                                time: format(new Date(event?.start), 'HH:mm'),
                                duration: 60,
                                event
                              });
                            }
                          }}
                        >
                          {event?.type === 'available' ? (
                            <>
                              <div>Disponível</div>
                              <div className="opacity-75">
                                {format(new Date(event?.start), 'HH:mm')}
                              </div>
                            </>
                          ) : (
                            'Ocupado'
                          )}
                        </div>
                      ))}
                      {dayEvents?.length > 3 && (
                        <div className="text-xs text-muted-foreground">
                          +{dayEvents?.length - 3} mais
                        </div>
                      )}
                      {availableEvents?.length > 0 && dayEvents?.length <= 3 && (
                        <div className="text-xs text-success/70 mt-1">
                          Clique para solicitar
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="calendar-container">
      {viewMode === 'week' ? <WeekView /> : <MonthView />}
      
      {/* Empty State */}
      {events?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Nenhuma disponibilidade
            </h3>
            <p className="text-muted-foreground mb-4">
              Este usuário não possui horários disponíveis configurados para este período.
            </p>
          </div>
        </div>
      )}

      {/* No Available Slots State */}
      {events?.length > 0 && !events?.some(event => isTimeSlotAvailable(event)) && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 m-4">
          <div className="flex items-center gap-2 text-warning">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">Nenhum horário disponível para agendamento</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Todos os horários deste período estão ocupados ou não são públicos.
          </p>
        </div>
      )}
    </div>
  );
};

export default UserCalendarGrid;